import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
interface SignInFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  onResetPassword: () => void;
}
export const SignInForm = ({
  email,
  setEmail,
  password,
  setPassword,
  onSubmit,
  loading,
  onResetPassword
}: SignInFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const isMobile = useIsMobile();
  
  
  const inputClasses = cn(
    "border-gray-200 focus:ring-primary focus:border-primary transition-all duration-200",
    "h-12 text-base", // Base height and text
    "px-3", // Normal horizontal padding (no icons)
    "sm:h-10 sm:text-sm" // Desktop sizing
  );
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }}>
      <form onSubmit={onSubmit} className="space-y-4">
        <motion.div className="space-y-2" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1,
        duration: 0.3
      }}>
          <Label htmlFor="email" className="font-medium">Email</Label>
          <Input 
            id="email" 
            type="email" 
            placeholder="email@example.com" 
            value={email} 
            onChange={e => setEmail(e.target.value)} 
            className={inputClasses}
            required 
            autoComplete="email" 
            aria-label="Email address" 
          />
        </motion.div>
        
        <motion.div className="space-y-2" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.3
      }}>
          <Label htmlFor="password" className="font-medium">Password</Label>
          <div className="relative">
            <Input 
              id="password" 
              type={showPassword ? "text" : "password"}
              placeholder="••••••••••" 
              value={password} 
              onChange={e => setPassword(e.target.value)} 
              className={cn(
                inputClasses,
                "pr-12" // Space for eye icon
              )}
              required 
              autoComplete="current-password"
              aria-label="Password"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "absolute right-0 top-0 px-3 py-2 hover:bg-transparent",
                isMobile ? "h-12 w-12 min-h-[44px] min-w-[44px]" : "h-full"
              )}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
          <div className="flex justify-end">
            <Button 
              type="button" 
              variant="link" 
              className={cn(
                "text-primary hover:text-primary/80",
                isMobile ? "p-2 h-auto min-h-[44px]" : "p-0 h-auto"
              )}
              onClick={onResetPassword}
            >
              Forgot password?
            </Button>
          </div>
        </motion.div>
        
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.3
      }}>
          <Button 
            type="submit" 
            className={cn(
              "w-full bg-primary hover:bg-primary/90 transition-colors mt-6",
              isMobile ? "h-12 text-base min-h-[44px]" : ""
            )}
            disabled={loading} 
            data-testid="sign-in-button"
          >
            {loading ? <>
                <span className="animate-spin mr-2 h-4 w-4 border-2 border-t-transparent border-white rounded-full"></span>
                Signing in...
              </> : "Sign In"}
          </Button>
        </motion.div>
        
        <motion.div 
          className="text-center mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <p className="text-xs text-muted-foreground">
            By signing in, you agree to our{" "}
            <Link 
              to="/terms-of-service" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link 
              to="/privacy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </motion.div>
      </form>
      
      <div className="mt-6 text-center">
        
      </div>
    </motion.div>;
};